package com.iregained.site.components.sections.news.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.news.styles.*
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

// UNIQUE ANIMATION: Ticker Slide (Horizontal cascade)
@Composable
private fun Modifier.tickerSlide(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateX(if (isVisible) 0.px else 50.px).transition(
        Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun NewsGridSection(data: NewsGridSectionData = defaultNewsGridData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG

    var selectedCategory by remember { mutableStateOf(NewsCategory.ALL) }
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "news-grid-section"
    var isVisible by remember { mutableStateOf(false) }
    val palette = ColorMode.current.toSitePalette()

    val groupedNews = remember(selectedCategory, data.items) {
        val filtered =
            if (selectedCategory == NewsCategory.ALL) data.items else data.items.filter { it.category == selectedCategory }
        filtered.sortedWith(compareByDescending<NewsItemData> { it.year }.thenByDescending { it.monthValue }
            .thenByDescending { it.day }).groupBy { it.year }
    }

    if (data.items.isNotEmpty()) {
        Column(
            modifier = NewsGridContainerStyle.toModifier().id(targetId).fillMaxWidth()
                .background(palette.custom.lightGray)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                    .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center).toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {

                    Row(modifier = FilterBarWrapperStyle.toModifier().tickerSlide(isVisible, 0)) {
                        NewsCategory.entries.forEach { category ->
                            key(category.name) {
                                val isActive = selectedCategory == category
                                val buttonStyle = if (isActive) FilterButtonStyle.toModifier()
                                    .then(FilterButtonActiveStyle.toModifier()) else FilterButtonStyle.toModifier()
                                Button(attrs = buttonStyle.onClick { selectedCategory = category }.toAttrs()) {
                                    Text(
                                        category.displayName
                                    )
                                }
                            }
                        }
                    }

                    if (groupedNews.isEmpty()) {
                        Box(
                            modifier = Modifier.fillMaxWidth().padding(topBottom = 60.px),
                            contentAlignment = Alignment.Center
                        ) {
                            SpanText(
                                text = "No updates found for this category yet.",
                                modifier = Modifier.fontSize(18.px).color(Colors.Gray)
                            )
                        }
                    } else {
                        groupedNews.forEach { (year, items) ->
                            key(year.toString()) {
                                Column(modifier = Modifier.fillMaxWidth().margin(bottom = 50.px)) {
                                    Box(
                                        modifier = Modifier.fillMaxWidth().margin(bottom = 25.px)
                                            .tickerSlide(isVisible, 150), contentAlignment = Alignment.Center
                                    ) {
                                        SectionHeader(
                                            text = year.toString(),
                                            fontSize = 24.px,
                                            lineWidthPercent = 25.percent,
                                            alignment = Alignment.CenterHorizontally
                                        )
                                    }
                                    SimpleGrid(
                                        numColumns(1, md = 2, lg = 3), modifier = NewsCardGridStyle.toModifier()
                                    ) {
                                        items.forEachIndexed { index, item ->
                                            key(item.headline) {
                                                // Stagger the horizontal slide for the cards
                                                NewsCard(item, Modifier.tickerSlide(isVisible, 300 + (index * 100)))
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun NewsCard(item: NewsItemData, modifier: Modifier = Modifier) {
    val palette = ColorMode.current.toSitePalette()
    if (item.linkUrl.isNotBlank()) {
        Link(
            path = item.linkUrl,
            modifier = Modifier.textDecorationLine(TextDecorationLine.None).fillMaxWidth().minWidth(0.px).then(modifier)
        ) {
            Box(
                modifier = NewsCardStyle.toModifier().fillMaxWidth().minWidth(0.px)
            ) {
                Column {
                    if (item.imagePath != null) {
                        Box(
                            modifier = NewsCardImageWrapperStyle.toModifier().fillMaxWidth().aspectRatio(16, 9)
                                .backgroundColor(palette.custom.lightGray).overflow(Overflow.Hidden),
                            contentAlignment = Alignment.Center
                        ) {
                            val isLikelyPhoto =
                                item.imagePath.endsWith(".jpg", ignoreCase = true) || item.imagePath.endsWith(
                                    ".jpeg", ignoreCase = true
                                )
                            Image(
                                src = item.imagePath,
                                alt = item.headline,
                                modifier = NewsCardImageStyle.toModifier().fillMaxSize()
                                    .objectFit(if (isLikelyPhoto) ObjectFit.Cover else ObjectFit.Contain)
                                    .padding(if (isLikelyPhoto) 0.px else 20.px)
                            )
                            if (item.isVideo) {
                                Box(modifier = VideoOverlayStyle.toModifier())
                                Box(
                                    modifier = PlayIconStyle.toModifier().position(Position.Absolute).bottom(16.px)
                                        .right(16.px).zIndex(2)
                                ) { Text("▶") }
                            }
                        }
                    }
                    Column(modifier = NewsCardContentStyle.toModifier()) {
                        Div(
                            attrs = NewsCardHeadlineStyle.toModifier().toAttrs {
                                ref { htmlElement ->
                                    htmlElement.innerHTML = item.headline
                                    onDispose {}
                                }
                            })

                        Row(modifier = NewsCardFooterStyle.toModifier()) {
                            SpanText(
                                text = item.sourceName,
                                modifier = NewsCardSourceStyle.toModifier().fontSize(16.px).color(palette.brand.primary)
                                    .fontWeight(FontWeight.Medium)
                            )
                            SpanText(
                                text = item.datePublished,
                                modifier = NewsCardDateStyle.toModifier().color(Colors.DarkGray).fontSize(16.px)
                                    .fontWeight(FontWeight.Bold)
                            )
                        }
                    }
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL & ARCHITECTURE
// ==========================================
/**
 * CMS CONTENT & IMAGE UPLOAD SPECS FOR NEWS GRID:
 * * @field imagePath (inside NewsItemData):
 * - Aspect Ratio: 16:9 (Standard Widescreen) or 1.91:1.
 * - Recommended Dimensions: 800x450px.
 * - Max File Size: 150KB (WebP or highly compressed JPEG).
 * - Format Constraints: Use solid-background WebP or JPEG for photography and article previews.
 * If uploading corporate PR logos (e.g., CTV News, Bloomberg), use transparent PNGs padded
 * evenly to fit the 16:9 container without stretching.
 * - Behavior: ObjectFit.Cover is applied to uniformly fill the card wrapper. Images missing from
 * text-only social proof posts will automatically collapse gracefully.
 * * @field date (inside NewsItemData):
 * - Format: MUST strictly follow the ISO-8601 string standard ("YYYY-MM-DD" e.g., "2026-05-11").
 * - Constraint: The automated Kotlin/JS sorting engine relies on this exact hyphenated format
 * to extract the `year`, `monthValue`, and `day` properties. Entering non-standard strings
 * (e.g., "May 11th, 2026") will default the year to 1970 and break the chronological grouping.
 * * @field headline (inside NewsItemData):
 * - Character Limit: Recommended 70–90 characters.
 * - Constraint: Long headlines will be truncated via Sveltia CMS pattern limits or line-clamp
 * CSS to prevent awkward vertical gaps across the 3-column grid.
 */
enum class NewsCategory(val displayName: String) {
    ALL("All Updates"), CLINICAL("Clinical Deployments"), REGULATORY("Regulatory & Compliance"), MEDIA("Press & Media"), SOCIAL(
        "Social Proof"
    )
}

data class NewsItemData(
    val category: NewsCategory,
    val headline: String,
    val sourceName: String,
    val date: String, // Expects standard CMS ISO format e.g., "2025-10-23"
    val linkUrl: String,
    val imagePath: String? = null,
    val isVideo: Boolean = false
) {
    // 1. Automatically extracts data for the Sorting Logic
    val year: Int get() = date.split("-").getOrNull(0)?.toIntOrNull() ?: 1970
    val monthValue: Int get() = date.split("-").getOrNull(1)?.toIntOrNull() ?: 1

    // Safely handles both "YYYY-MM-DD" and "YYYY-MM-DDThh:mm:ssZ" formats
    val day: Int get() = date.split("-").getOrNull(2)?.substringBefore("T")?.toIntOrNull() ?: 1

    // 2. Automatically generates the full string for the Section Headers
    val month: String
        get() = when (monthValue) {
            1 -> "January"; 2 -> "February"; 3 -> "March"; 4 -> "April"
            5 -> "May"; 6 -> "June"; 7 -> "July"; 8 -> "August"
            9 -> "September"; 10 -> "October"; 11 -> "November"; 12 -> "December"
            else -> ""
        }

    // 3. Automatically generates the display string for the Card (e.g., "Oct 23")
    val datePublished: String
        get() {
            val shortMonth = month.take(3)
            val paddedDay = day.toString().padStart(2, '0')
            return "$shortMonth $paddedDay"
        }
}

data class NewsGridSectionData(
    val sectionId: String? = "news-grid-section", val items: List<NewsItemData> = listOf(
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "Neuroplasticity in Action: IRegained's MyHand<sup>®</sup> System Restores Hand Mobility for Stroke Survivors",
            sourceName = "Innovation Factory",
            date = "2025-10-23", // Single ISO date string
            linkUrl = "https://innovationfactory.ca/advancing-stroke-rehabilitation-with-iregaineds-myhand-system/?utm_source=newsletter&utm_medium=email&utm_campaign=cameda",
            imagePath = Res.Images.News.Grid.INNOVATION_FACTORY
        ),
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "IRegained Showcased at ASU InvestU’s Health Tech Pitch Event",
            sourceName = "ASU InvestU",
            date = "2025-09-30",
            linkUrl = "https://www.linkedin.com/posts/asu-investu_healthtech-medtech-investu-activity-7384302620829659136-o9Sb?utm_source=li_share&utm_content=feedcontent&utm_medium=g_dt_web&utm_campaign=copy",
            imagePath = Res.Images.News.Grid.ASU_INVESTU,
        ),
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "Sudbury med-tech device approved for sale in India",
            sourceName = "Sudbury.Com",
            date = "2025-07-30",
            linkUrl = "https://www.sudbury.com/local-business/sudbury-med-tech-device-approved-for-sale-in-india-11010892",
            imagePath = Res.Images.News.Grid.SUDBURY_COM,
        ),
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "Sudbury med-tech startup reaches Canadian distribution agreement",
            sourceName = "Northern Ontario Business",
            date = "2025-05-14",
            linkUrl = "https://www.northernontariobusiness.com/industry-news/technology/sudbury-med-tech-startup-reaches-canadian-distribution-agreement-10649168",
            imagePath = Res.Images.News.Grid.NORTHERN_ONTARIO_BUSINESS,
        ),
        NewsItemData(
            category = NewsCategory.SOCIAL,
            headline = "Exciting News from MapleCare Physio",
            sourceName = "LinkedIn",
            date = "2025-04-05",
            linkUrl = "https://www.linkedin.com/posts/iregained-inc_strokerecovery-neuroplasticity-iregained-activity-7309661232783835136-jYky?utm_source=share&utm_medium=member_desktop&rcm=ACoAADch89sBKQOTn0YvY0DhIUk7vIGzFjP4zKA",
            imagePath = Res.Images.News.Grid.LINKEDIN
        ),
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "Retrain the Brain, Not the Limbs",
            sourceName = "Canadian Business",
            date = "2025-03-06",
            linkUrl = "https://canadianbusiness.com/ideas/retrain-the-brain-not-the-limbs/",
            imagePath = Res.Images.News.Grid.CANADIAN_BUSINESS
        ),
        NewsItemData(
            category = NewsCategory.SOCIAL,
            headline = "Know Stroke Podcast",
            sourceName = "LinkedIn",
            date = "2024-12-06",
            linkUrl = "https://www.linkedin.com/posts/knowstrokepodcast_strokerehabilitation-innovation-knowstrokepodcast-activity-7270815004638023680-_hzZ?utm_source=share&utm_medium=member_desktop",
            imagePath = Res.Images.News.Grid.LINKEDIN
        ),
        NewsItemData(
            category = NewsCategory.SOCIAL,
            headline = "IRegained journey of launching our MyHand<sup>®</sup> System in the market",
            sourceName = "LinkedIn",
            date = "2023-07-11",
            linkUrl = "https://www.linkedin.com/feed/update/urn:li:activity:7084728966455967744",
            imagePath = Res.Images.News.Grid.LINKEDIN
        ),
        NewsItemData(
            category = NewsCategory.SOCIAL,
            headline = "IRegained Inc is proud to share that we have hit our first big commercialization success",
            sourceName = "LinkedIn",
            date = "2023-07-10",
            linkUrl = "https://www.linkedin.com/feed/update/urn:li:activity:7085420580392312832",
            imagePath = Res.Images.News.Grid.LINKEDIN
        ),
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "Business of the Month: Following FDA approval, IRegained ready to tackle U.S. market",
            sourceName = "Northern Ontario Business",
            date = "2023-04-03",
            linkUrl = "https://www.northernontariobusiness.com/business-of-the-month/business-of-the-month-following-fda-approval-iregained-ready-to-tackle-us-market-6598094",
            imagePath = Res.Images.News.Grid.NORTHERN_ONTARIO_BUSINESS,
        ),
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "IRegained Inc. the Sudbury medtech firm takes its device to the cloud",
            sourceName = "Northern Ontario Business",
            date = "2022-11-12",
            linkUrl = "https://www.sudbury.com/local-news/sudbury-medtech-firm-takes-its-device-to-the-cloud-6094709",
            imagePath = Res.Images.News.Grid.NORTHERN_ONTARIO_BUSINESS
        ),
        NewsItemData(
            category = NewsCategory.SOCIAL,
            headline = "IRegained Inc. is thrilled to announce the official launch of collaboration with Wistron Medical and TMU",
            sourceName = "LinkedIn",
            date = "2022-11-11",
            linkUrl = "https://www.linkedin.com/embed/feed/update/urn:li:share:6997769967878250496",
            imagePath = Res.Images.News.Grid.LINKEDIN
        ),
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "IRegained seeking to commercialize hand rehabilitation system with CTA and TMU",
            sourceName = "DigiTimes Asia",
            date = "2022-10-31",
            linkUrl = "https://www.digitimes.com/news/a20221020VL206.html",
            imagePath = Res.Images.News.Grid.DIGITIMES_ASIA
        ),
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "Techstination Interview: Vineet Johnson",
            sourceName = "Techstination",
            date = "2022-08-09",
            linkUrl = "https://www.techstination.com/interview.jsp?interviewId=4798",
            imagePath = Res.Images.News.Grid.TECHSTINATION
        ),
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "Workforce growth expect at Sudbury start-up developing stroke rehabilitation device",
            sourceName = "CBC News",
            date = "2022-08-24",
            linkUrl = "https://www.cbc.ca/news/canada/sudbury/iregained-myhand-system-growing-workforce-1.6559959",
            imagePath = Res.Images.News.Grid.CBC_NEWS
        ),
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "Vineet Johnson, CEO and Founder of the IRegained Inc. at CA exclusive interview",
            sourceName = "Made in CA",
            date = "2022-07-18",
            linkUrl = "https://madeinca.ca/vineet-johnson-iregained/",
            imagePath = Res.Images.News.Grid.MADE_IN_CA
        ),
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "IRegained Inc. - Sudbury med-tech startup takes important step",
            sourceName = "The Sudbury Star",
            date = "2022-06-09",
            linkUrl = "https://www.thesudburystar.com/news/local-news/sudbury-med-tech-startup-takes-important-step",
            imagePath = Res.Images.News.Grid.THE_SUDBURY_STAR
        ),
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "IRegained Inc. opens doors to new office in Sudbury, Ontario",
            sourceName = "Sudbury.Com",
            date = "2022-06-12",
            linkUrl = "https://www.sudbury.com/local-news/sudbury-med-tech-startup-opens-doors-to-new-office-5468449",
            imagePath = Res.Images.News.Grid.SUDBURY_COM
        ),
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "IRegained at 25th annual Bell Business Excellence Awards",
            sourceName = "Sudbury.Com",
            date = "2022-05-27",
            linkUrl = "https://www.sudbury.com/local-news/sudbury-chamber-celebrates-local-businesses-5414789#:~:text=The%20award%20winners%20for%20the,Excellence%3A%20Kuppajo%20Espresso%20Bar%20Inc",
            imagePath = Res.Images.News.Grid.SUDBURY_COM
        ),
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "IRegained in Angel Investors Ontario 15-Year Anniversary Special Publication",
            sourceName = "Issuu",
            date = "2022-05-25",
            linkUrl = "https://issuu.com/angelinvestorson/docs/aio_xv_magazine_digital/98",
            imagePath = Res.Images.News.Grid.ISSUU
        ),
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "New device designed in Sudbury helps stroke patients regain mobility",
            sourceName = "CTV News",
            date = "2022-04-21",
            linkUrl = "https://www.ctvnews.ca/northern-ontario/article/new-device-designed-in-sudbury-helps-stroke-patients-regain-mobility/",
            imagePath = Res.Images.News.Grid.CTV_NEWS
        ),
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "IRegained Inc Graduated from Octane LaunchPad",
            sourceName = "Octane LaunchPad",
            date = "2021-09-29",
            linkUrl = "https://blog.octaneoc.org/launchpad-announces-top-8-presenting-companies-at-medtech-innovation-forum-2021",
            imagePath = Res.Images.News.Grid.OCTANE
        ),
        NewsItemData(
            category = NewsCategory.SOCIAL,
            headline = "IRegained is Transforming Post-Stroke Care with its Innovative MyHand<sup>®</sup> Rehabilitative Device",
            sourceName = "Youtube - MedTech Trends",
            date = "2021-07-07",
            linkUrl = "https://www.youtube.com/watch?v=P0yf120X0H8",
            imagePath = Res.Images.News.Grid.YOUTUBE,
            isVideo = true
        ),
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "Sudbury technology helping people recover from stroke",
            sourceName = "The Sudbury Star",
            date = "2021-04-15",
            linkUrl = "https://www.thesudburystar.com/news/local-news/sudbury-technology-helping-people-recover-from-stroke",
            imagePath = Res.Images.News.Grid.THE_SUDBURY_STAR
        ),
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "IRegained featured by Changerangers.coo for being selected in CABHI 2021 SUMMIT: Accelerating Innovation in Age-Tech",
            sourceName = "Change Rangers",
            date = "2021-03-30",
            linkUrl = "https://www.changerangers.com/technology-aging-longevity/2021/accelerating-innovation-in-age-tech-an-exhilarating-cabhi-2021/",
            imagePath = Res.Images.News.Grid.CHANGE_RANGERS
        ),
        NewsItemData(
            category = NewsCategory.SOCIAL,
            headline = "IRegained Inc is a member of Better Way Alliance",
            sourceName = "Twitter X",
            date = "2020-02-11",
            linkUrl = "https://x.com/BetterWayCAN/status/1227336633202155520",
            imagePath = Res.Images.News.Grid.TWITTER_X
        ),
        NewsItemData(
            category = NewsCategory.SOCIAL,
            headline = "IRegained Inc. at Keiretsu Forum",
            sourceName = "LinkedIn",
            date = "2020-02-01",
            linkUrl = "https://www.linkedin.com/posts/vineetjohnson_iregained-kieretsu-forum-activity-6625807256019587072-WzCX",
            imagePath = Res.Images.News.Grid.LINKEDIN
        ),
        NewsItemData(
            category = NewsCategory.SOCIAL,
            headline = "IRegained Inc pitch at Northern Ontario Angels, Timmins, Ontario",
            sourceName = "LinkedIn",
            date = "2019-10-30",
            linkUrl = "https://www.linkedin.com/posts/vineetjohnson_iregained-activity-6592240040305274880-VjkG",
            imagePath = Res.Images.News.Grid.LINKEDIN
        ),
        NewsItemData(
            category = NewsCategory.MEDIA,
            headline = "IRegained and its cofounder find a home in Sudbury, Ontario, CA",
            sourceName = "Northern Ontario Business",
            date = "2019-08-23",
            linkUrl = "https://www.northernontariobusiness.com/regional-news/sudbury/iregained-and-its-cofounder-find-a-home-in-sudbury-1654903",
            imagePath = Res.Images.News.Grid.NORTHERN_ONTARIO_BUSINESS
        ),
        NewsItemData(
            category = NewsCategory.SOCIAL,
            headline = "IRegained Inc. – PITCH 2019 | NORCAT Innovation",
            sourceName = "Youtube - Norcat Innovation",
            date = "2019-06-13",
            linkUrl = "https://youtu.be/8EqeeGcAz-I",
            imagePath = Res.Images.News.Grid.YOUTUBE,
            isVideo = true
        ),
    )
)

val defaultNewsGridData = NewsGridSectionData()